import React from 'react';
import { Tooltip, Typography } from '@mui/material';
import { numberToCurrencyString } from 'further-ui/utils';
import {
  LpFeeCalculationBasisLabels,
  LpFeeCommitmentStatusLabels,
} from 'further-ui/labels';
import { Column as LayoutColumn } from 'further-ui/components/Layout';
import { EqualisationFeesFormValues } from './FormWrapper';
import useEqualiseFees from '../../hooks/useEqualiseFees';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import NumberInput from 'further-ui/components/NumberInput';
import TooltipTriggerText from 'components/new/TooltipTriggerText';

interface FeeCardRowProps {
  index: number;
  value: EqualisationFeesFormValues['totalCommitmentFees'][number];
  baseCurrency: string;
  onChange: (
    value: EqualisationFeesFormValues['totalCommitmentFees'][number],
  ) => void;
}

const FeeCardRow: React.FC<FeeCardRowProps> = ({
  index,
  value,
  baseCurrency,
  onChange,
}) => {
  const { updateSingleFeeField } = useEqualiseFees();

  const rows = (value?.lpFees ?? []).map((fee, feeIndex) => ({
    ...fee,
    id: fee.id || `${index}-${feeIndex}`,
    baseCurrency,
    rowIndex: feeIndex,
  }));

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'accrualDate',
      label: 'Accrual date',
      width: 140,
      render: (row) => row.accrualDate.toReadable(),
    },
    {
      key: 'feeTemplateName',
      label: 'Fee template name',
      width: 180,
      render: (row) => (
        <Tooltip
          title={
            <LayoutColumn spacing="xxs">
              <Typography variant="caption" color="inherit">
                <b>Inside/outside commitment:</b>{' '}
                {LpFeeCommitmentStatusLabels.get(row.insideOutsideCommitment)}
              </Typography>
              <Typography variant="caption" color="inherit">
                <b>Calculation basis:</b>{' '}
                {LpFeeCalculationBasisLabels.get(row.calculationBasis)}
              </Typography>
            </LayoutColumn>
          }
        >
          <TooltipTriggerText variant="h6" content={row.feeTemplateName} />
        </Tooltip>
      ),
    },
    {
      key: 'totalCommitmentLcy',
      label: 'Total commitment',
      width: 160,
      render: (row) =>
        numberToCurrencyString(row.totalCommitmentLcy, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
    },
    {
      key: 'equalisationFeeExTaxLcy',
      label: 'Fee (ex tax)',
      width: 160,
      render: (row) => (
        <NumberInput
          value={row.equalisationFeeExTaxLcy}
          currencyStartAdornment
          currency={row.baseCurrency}
          decimals={2}
          fixedDecimalScale
          onChange={(newFeeValue) => {
            onChange(
              updateSingleFeeField(
                value,
                'equalisationFeeExTaxLcy',
                newFeeValue ?? 0,
                row.rowIndex,
              ),
            );
          }}
        />
      ),
    },
    {
      key: 'equalisationSalesTaxAmountLcy',
      label: 'Sales tax',
      width: 160,
      render: (row) => (
        <NumberInput
          value={row.equalisationSalesTaxAmountLcy}
          currencyStartAdornment
          decimals={2}
          fixedDecimalScale
          currency={row.baseCurrency}
          onChange={(newFeeValue) => {
            onChange(
              updateSingleFeeField(
                value,
                'equalisationSalesTaxAmountLcy',
                newFeeValue ?? 0,
                row.rowIndex,
              ),
            );
          }}
        />
      ),
    },
    {
      key: 'equalisationFeeIncTaxLcy',
      label: 'Fee (inc tax)',
      width: 160,
      render: (row) =>
        numberToCurrencyString(row.equalisationFeeIncTaxLcy, {
          fallback: '-',
          currency: row.baseCurrency,
        }),
    },
  ];

  return (
    <Table
      emptyMessage="No fees"
      scrollable
      showHeaderBorder
      showTopBorder
      columns={columns}
      rows={rows}
    />
  );
};

export default FeeCardRow;
